import { zipJobs, createSharedLink } from '../lib/api';
import config from '../config/config';
import { uploadFile, downloadUrl, isTerminalJobStatus, trackAnyResultDownloaded } from '../lib/utils';
import {
    getMixpanelDeviceId,
    identifyPublicHomeUser,
    trackHomeAbandon,
    signupSourceForVariant,
    trackHomeSignupClient,
} from './analytics';
import {
    applyVariantToUploadOptions,
    getPublicHomeVariant,
    getVariantSayLines,
    initPublicHomeVariants,
    trackHomeEvent,
} from './variants';

const API = '/api/public-home';
const CAP = 5 * 1024 * 1024 * 1024;
const POLL_INTERVAL_MS = 5000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

function $(id) { return document.getElementById(id); }

function gb(bytes) {
    const b = Number(bytes) || 0;
    if (b >= 1073741824) return `${(b / 1073741824).toFixed(1)} GB`;
    if (b >= 10485760) return `${Math.round(b / 1048576)} MB`;
    if (b >= 1048576) return `${(b / 1048576).toFixed(1)} MB`;
    return `${Math.max(1, Math.round(b / 1024))} KB`;
}

function lighterPct(input, output) {
    if (!input || !output || output >= input) return 0;
    return Math.round((1 - output / input) * 100);
}

const RAW_EXT = /^(CR2|CR3|CRW|NEF|NRW|ARW|SRF|SR2|DNG|RAF|ORF|RW2|PEF|SRW|X3F|3FR|IIQ|ERF|MRW|KDC|DCR|MOS|RWL|GPR)$/;

function extOf(f) {
    const parts = (f?.name || '').split('.');
    const ext = parts.length > 1 ? parts.pop().toUpperCase() : '';
    return ext && ext.length <= 5 ? ext : '';
}

/** "PNG", "PNG and TIFF", "PNG, TIFF and GIF" (max 3 named) */
function listExts(exts) {
    const u = [...new Set(exts.filter(Boolean))].slice(0, 3);
    if (u.length <= 1) return u[0] || '';
    return `${u.slice(0, -1).join(', ')} and ${u[u.length - 1]}`;
}

function unsupportedMessage(skipped) {
    const exts = skipped.map(extOf);
    const raws = exts.filter((e) => RAW_EXT.test(e));
    if (raws.length && raws.length === exts.length) {
        return `RAW files (${listExts(raws)}) aren't supported. Export them as JPEG, then add them here.`;
    }
    const named = listExts(exts);
    if (!named) return 'These files aren\'t supported. Add JPEG, HEIC, MP4 or MOV.';
    return `${named} files aren't supported. Add JPEG, HEIC, MP4 or MOV.`;
}

function friendlyUploadError(err, file) {
    const raw = (err && err.message) || '';
    if (/unsupported file type/i.test(raw)) {
        const ext = ((file?.name || '').split('.').pop() || '').toUpperCase();
        if (/^(JPE?G|HEIC|HEIF|MP4|MOV)$/.test(ext)) {
            return `We couldn't read ${file.name}. Export it again as JPEG, HEIC, MP4 or MOV and retry.`;
        }
        return `${ext && ext.length <= 5 ? `${ext} files aren't` : "That file type isn't"} supported yet. Try JPEG, HEIC, MP4 or MOV.`;
    }
    return 'The upload didn\'t go through. Check your connection and try again.';
}

function focusInCard(el) {
    if (!el) return;
    el.focus({ preventScroll: true });
    const card = el.closest('.card');
    if (!card) return;
    const r = el.getBoundingClientRect();
    const c = card.getBoundingClientRect();
    if (r.bottom > c.bottom - 12) card.scrollTop += r.bottom - c.bottom + 24;
    else if (r.top < c.top + 12) card.scrollTop -= c.top - r.top + 24;
}

const isVid = (f) => /\.(mp4|mov)$/i.test(f.name) || /^video\//.test(f.type);
const isHeic = (f) => /\.(heic|heif)$/i.test(f.name) || /^image\/hei[cf]$/i.test(f.type);
const isJpg = (f) => /\.jpe?g$/i.test(f.name) || f.type === 'image/jpeg';
const okEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

async function apiPost(path, body, csrfToken) {
    const res = await fetch(`${API}/${path}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(csrfToken ? { 'x-csrf-token': csrfToken } : {}),
        },
        body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        throw new Error(data.error || 'Request failed');
    }
    if (data.success === false) {
        throw new Error(data.error || 'Request failed');
    }
    return data;
}

export function initPublicHomeUpload({ csrfToken }) {
    let files = [];
    let publicHomeToken = null;
    let verified = false;
    let userEmail = '';
    let mode = 'email';
    let suggested = '';
    let t0 = 0;
    let pollTimer = null;
    let pollStartedAt = null;
    let optimizationTimedOut = false;
    let uploadAbort = null;
    let statusSnapshot = { progress: 0, done: false, input_bytes: 0, output_bytes: 0 };
    /** Job ids for this page load / upload attempt only (cleared on reload). */
    let batchJobIds = [];
    let queuedDelivery = null;
    let downloadContext = null;
    let doneShareUrl = '';
    let doneProjectId = null;
    let doneProjectName = '';
    let sayNow = 'add';
    let busy = false;
    let currentFile = null;
    let uploadMethod = 'picker';
    let signupPanelTracked = false;
    let abandonSent = false;
    const acctTitle = document.querySelector('[data-copy="qAcct"]');
    const acctTitleDefault = acctTitle?.textContent || '';

    function applyShareUrlToUi(fullUrl) {
        if ($('linkOut')) $('linkOut').value = fullUrl.replace(/^https?:\/\//, '');
        if ($('openG')) $('openG').href = fullUrl;
    }

    function fullShareUrlFromInput() {
        const raw = ($('linkOut')?.value || '').trim();
        if (!raw) return '';
        return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    }

    function copyShareLink(button) {
        const url = fullShareUrlFromInput();
        if (!url) return;
        const btn = button || $('copy');
        navigator.clipboard.writeText(url).then(() => {
            if (btn) btn.textContent = 'Copied';
        }).catch(() => $('linkOut')?.select());
    }

    function shareShareLink() {
        const url = fullShareUrlFromInput();
        if (!url) return;
        const title = doneProjectName || $('wname')?.value?.trim() || 'Your photos and films';
        if (navigator.share) {
            navigator.share({
                title,
                text: 'Here are your photos and films, in one link.',
                url,
            }).catch(() => {});
            return;
        }
        try {
            navigator.clipboard.writeText(url).then(() => {
                if ($('toast')) $('toast').textContent = 'Link copied. Paste it anywhere.';
            });
        } catch (e) {
            $('linkOut')?.select();
        }
    }

    async function ensureDoneShareUrl() {
        if (doneShareUrl) return doneShareUrl;
        if (!doneProjectId) return '';
        const { token } = await createSharedLink({
            project_id: doneProjectId,
            title: doneProjectName || 'Gallery',
            subtitle: '',
        });
        doneShareUrl = `${window.location.origin}/v/${token}`;
        return doneShareUrl;
    }

    async function showShareLinkAfterDownload() {
        $('dDown').hidden = true;
        $('dLink').hidden = false;
        $('dH').textContent = 'Your gallery is ready';
        try {
            const url = await ensureDoneShareUrl();
            if (!url) {
                if ($('toast')) $('toast').textContent = 'Could not create your link. Try again.';
                return;
            }
            applyShareUrlToUi(url);
            if ($('toast')) $('toast').textContent = '';
            focusInCard($('copy'));
        } catch (err) {
            console.error(err);
            if ($('toast')) $('toast').textContent = 'Could not create your link. Try again.';
        }
    }

    function statusUrl() {
        if (!batchJobIds.length) return `${API}/status`;
        return `${API}/status?job_ids=${encodeURIComponent(batchJobIds.join(','))}`;
    }

    function focusCard() {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        const card = document.querySelector('.card');
        setTimeout(() => {
            card?.classList.remove('spot');
            void card?.offsetWidth;
            card?.classList.add('spot');
            const target = !$('pAdd')?.hidden ? $('addBtn') : card?.querySelector('input, button');
            target?.focus({ preventScroll: true });
        }, 420);
    }

    function setMode(m) {
        if (!m) return;
        mode = m;
        if ($('eToMail')) $('eToMail').textContent = '';
        document.querySelectorAll('.opt[data-m]').forEach((x) => {
            x.setAttribute('aria-checked', x.dataset.m === m ? 'true' : 'false');
        });
        $('toFields').hidden = mode !== 'email';
        const T = getPublicHomeVariant().T || {};
        const goLabels = T.go || { email: 'Send to my client', link: 'Get my link', download: 'Download optimized files' };
        $('go').textContent = goLabels[m] || goLabels.email;
        if (mode === 'email') focusInCard($('to'));
    }

    initPublicHomeVariants({ focusCard, setMode });
    applyVariantToUploadOptions({ setMode });

    const variantSay = getVariantSayLines();
    const SAY = {
        add: variantSay.add || ['Drop the whole wedding. <span>Get one link back.</span>', 'Photos and films, a fraction of the size, visually identical. Ready to share in minutes.', true],
        work: variantSay.work || ['Watch it shrink. <span>Not a pixel less.</span>', 'Create your free account while it uploads. We\'ll save everything to it for you.', false],
        workIn: ['Watch it shrink. <span>Not a pixel less.</span>', 'It\'s saving to your account. Now tell us who gets it.', false],
        done: variantSay.done || ['That was fast. <span>Now send it.</span>', 'One link for the whole shoot. It opens instantly, even on a phone.', false],
        doneEmail: ['Sent. <span>They\'ll have it in a minute.</span>', 'One link for the whole shoot. It opens instantly, even on a phone.', false],
        doneDownload: ['Done. <span>Your files are ready.</span>', 'Full resolution, visually identical. A copy stays in your account.', false],
    };

    function paintSay(k) {
        if ($('sayH')) $('sayH').innerHTML = SAY[k][0];
        if ($('sayP')) $('sayP').textContent = SAY[k][1];
        const el = $('say');
        if (el) el.dataset.step = k;
        sayNow = k;
    }

    function say(k) {
        if (k === sayNow) return;
        const el = $('say');
        const put = () => {
            paintSay(k);
            el?.classList.remove('swap');
        };
        if (!el) return put();
        el.classList.add('swap');
        setTimeout(put, 300);
    }

    function show(id, sayKey) {
        ['pAdd', 'pWork', 'pDone'].forEach((x) => { if ($(x)) $(x).hidden = x !== id; });
        say(sayKey || { pAdd: 'add', pWork: 'work', pDone: 'done' }[id]);
    }

    // HTML ships wedding copy; variant SAY is applied after init — always paint once.
    paintSay('add');
    ['pAdd', 'pWork', 'pDone'].forEach((x) => {
        if ($(x)) $(x).hidden = x !== 'pAdd';
    });

    const RING_LEN = 452.4;
    const MINI_LEN = 100.53;

    function setProgressIndeterminate(on) {
        $('miniRing')?.classList.toggle('indeterminate', on);
        $('ring')?.classList.toggle('indeterminate', on);
        const ring = $('ring');
        if (ring) {
            if (on) {
                ring.setAttribute('aria-busy', 'true');
                ring.removeAttribute('aria-valuenow');
            } else {
                ring.removeAttribute('aria-busy');
            }
        }
        if (on) {
            $('miniFg')?.style.removeProperty('stroke-dashoffset');
            $('miniFg')?.style.removeProperty('stroke-dasharray');
            $('ringFg')?.style.removeProperty('stroke-dashoffset');
            $('ringFg')?.style.removeProperty('stroke-dasharray');
        }
    }

    function paintSizeLine(input, output) {
        const now = output > 0 ? output : input;
        if ($('mSize')) {
            $('mSize').innerHTML = output > 0
                ? `${gb(input)} → <span class="gr">${gb(output)}</span>`
                : gb(input);
        }
        $('szFrom') && ($('szFrom').textContent = gb(input));
        $('szNow') && ($('szNow').textContent = gb(now));
        // Before/after only means something once there's an "after": hide it while uploading
        const sizeRow = $('szFrom')?.closest('.size');
        if (sizeRow) sizeRow.hidden = !(output > 0 && output < input);
        if ($('szNote')) {
            const light = input && output ? Math.round((1 - output / input) * 100) : 0;
            $('szNote').textContent = `${light ? `${light}% lighter. ` : ''}Full resolution, visually identical.`;
        }
    }

    function keepOpen(text) {
        const el = $('keepOpen');
        if (!el) return;
        el.textContent = text || '';
        el.hidden = !text || !busy;
    }

    function paintProgress(uploadPct, status, phase = 'upload') {
        const input = status?.input_bytes || files.reduce((s, f) => s + f.size, 0);
        const output = status?.output_bytes || 0;

        if (phase === 'complete') {
            setProgressIndeterminate(false);
            $('ring')?.classList.add('done');
            $('mPct') && ($('mPct').textContent = 'Ready');
            paintSizeLine(input, output || input);
            if (acctTitle && !verified) acctTitle.textContent = 'It\'s ready. Create your free account to send it.';
            keepOpen('Keep this tab open until you send it.');
            $('miniFg') && ($('miniFg').style.strokeDashoffset = '0');
            $('pct') && ($('pct').textContent = '100%');
            $('ringFg') && ($('ringFg').style.strokeDashoffset = '0');
            $('ring')?.setAttribute('aria-valuenow', '100');
            if ($('pctLbl')) $('pctLbl').textContent = 'Gallery ready';
            return;
        }

        if (phase === 'optimizing') {
            setProgressIndeterminate(true);
            $('ring')?.classList.remove('done');
            $('mPct') && ($('mPct').textContent = 'Optimizing');
            paintSizeLine(input, output);
            keepOpen('Keep this tab open while we optimize your files.');
            $('pct') && ($('pct').textContent = '…');
            if ($('pctLbl')) $('pctLbl').textContent = 'Optimizing';
            return;
        }

        setProgressIndeterminate(false);
        $('ring')?.classList.remove('done');
        const pct = Math.min(100, Math.round(uploadPct));
        $('mPct') && ($('mPct').textContent = pct >= 100 ? 'Optimizing' : `Uploading ${pct}%`);
        keepOpen('Keep this tab open until the upload finishes.');
        paintSizeLine(input, 0);
        $('miniFg') && ($('miniFg').style.strokeDashoffset = `${MINI_LEN * (1 - pct / 100)}`);
        $('pct') && ($('pct').textContent = `${pct}%`);
        $('ringFg') && ($('ringFg').style.strokeDashoffset = `${RING_LEN * (1 - pct / 100)}`);
        $('ring')?.setAttribute('aria-valuenow', String(pct));
        if ($('pctLbl')) $('pctLbl').textContent = pct >= 100 ? 'Uploaded' : 'Uploading';
    }

    function batchStats() {
        const batch_size = files.length;
        const total_size_mb = Math.round(files.reduce((s, f) => s + f.size, 0) / (1024 * 1024));
        return { batch_size, total_size_mb };
    }

    function trackUploadStarted() {
        const { batch_size, total_size_mb } = batchStats();
        const images = files.filter((f) => !isVid(f)).length;
        const videos = files.filter((f) => isVid(f)).length;
        const base = { page: 'home', method: uploadMethod, batch_size, total_size_mb };
        if (images) trackHomeEvent('image_upload_started', base);
        if (videos) trackHomeEvent('video_upload_started', base);
    }

    function ask(k) {
        document.querySelectorAll('#flow .qs').forEach((el) => {
            el.classList.toggle('on', el.dataset.q === k);
        });
        $('mini')?.classList.toggle('hide', k === 'wait');
        if (k === 'acct' && !signupPanelTracked) {
            signupPanelTracked = true;
            const { batch_size, total_size_mb } = batchStats();
            trackHomeEvent('home_signup_panel_shown', {
                files_ready: Boolean(statusSnapshot.done),
                batch_size,
                total_size_mb,
            });
        }
        if (k === 'name' && suggested && !$('wname').value) {
            $('wname').value = suggested;
            $('nameSub').textContent = 'From your folder name. Press Enter to keep it.';
            setTimeout(() => $('wname')?.select(), 80);
        }
        const focus = { acct: 'email', code: 'code', name: 'wname', to: mode === 'email' ? 'to' : 'go' }[k];
        if (k === 'acct') $('eAcct').textContent = '';
        ['eCode', 'eName', 'eTo', 'eToMail'].forEach((id) => { if ($(id)) $(id).textContent = ''; });
        const card = document.querySelector('.card');
        if (card) card.scrollTop = 0;
        if (focus) setTimeout(() => focusInCard($(focus)), 60);
    }

    function stopStatusPolling() {
        if (pollTimer) {
            clearInterval(pollTimer);
            pollTimer = null;
        }
        pollStartedAt = null;
    }

    function showWorkError(message) {
        const text = message || 'Something went wrong. Please try again.';
        setProgressIndeterminate(false);
        if ($('waitNote')) $('waitNote').textContent = text;
        if ($('szNote')) $('szNote').textContent = text;
        if ($('eTo')) $('eTo').textContent = text;
        if ($('eWork')) $('eWork').textContent = text;
        if ($('mPct')) $('mPct').textContent = 'Problem';
        if ($('pctLbl')) $('pctLbl').textContent = 'Problem';
    }

    function abortOptimizationWait(message) {
        if (optimizationTimedOut) return;
        optimizationTimedOut = true;
        stopStatusPolling();
        showWorkError(message);
    }

    async function pollStatus() {
        if (optimizationTimedOut) return;

        if (pollStartedAt && Date.now() - pollStartedAt > POLL_TIMEOUT_MS) {
            abortOptimizationWait(
                'This is taking longer than 5 minutes. You can keep this page open, or sign in from the app later to see your files. If nothing appears, try uploading again.',
            );
            return;
        }

        try {
            const res = await fetch(statusUrl());
            const data = await res.json();
            if (!data.success) return;
            statusSnapshot = data;

            const jobs = data.jobs || [];

            if (data.done && jobs.length > 0) {
                paintProgress(100, data, 'complete');
                if (verified && queuedDelivery) {
                    stopStatusPolling();
                    try {
                        await finalizeDelivery(queuedDelivery, data);
                    } catch (err) {
                        console.error(err);
                        optimizationTimedOut = false;
                        showWorkError(err.message || 'Could not finish setup. Try again.');
                    }
                } else {
                    stopStatusPolling();
                }
            } else {
                paintProgress(100, data, 'optimizing');
            }
        } catch (err) {
            console.warn('[public-home] status poll', err);
        }
    }

    async function uploadAll() {
        const totalBytes = files.reduce((s, f) => s + f.size, 0) || 1;
        let completedBytes = 0;
        for (const file of files) {
            currentFile = file;
            const uploadRequest = await apiPost('upload', {
                filename: file.name,
                type: file.type,
                size: file.size,
            });
            publicHomeToken = uploadRequest.public_home_token || publicHomeToken;

            const { done, abort } = uploadFile(
                file,
                uploadRequest,
                (fileFrac) => {
                    const frac = Math.min(1, Math.max(0, Number(fileFrac) || 0));
                    const overall = ((completedBytes + frac * file.size) / totalBytes) * 100;
                    paintProgress(overall, statusSnapshot, 'upload');
                },
                {
                    requestPartUrl: (part_number) => apiPost('upload/multipart/part', {
                        session_id: uploadRequest.session_id,
                        filename: file.name,
                        upload_id: uploadRequest.upload_id,
                        part_number,
                    }),
                    completeMultipartUpload: (parts) => apiPost('upload/multipart/complete', {
                        session_id: uploadRequest.session_id,
                        filename: file.name,
                        upload_id: uploadRequest.upload_id,
                        parts,
                    }),
                    abortMultipartUpload: () => apiPost('upload/multipart/abort', {
                        session_id: uploadRequest.session_id,
                        filename: file.name,
                        upload_id: uploadRequest.upload_id,
                    }),
                },
            );
            uploadAbort = abort;
            await done();
            completedBytes += file.size;
            paintProgress((completedBytes / totalBytes) * 100, statusSnapshot, 'upload');

            const submitRes = await apiPost('jobs/submit', {
                public_home_token: publicHomeToken,
                session_id: uploadRequest.session_id,
                filename: file.name,
                size: file.size,
                mixpanel_device_id: getMixpanelDeviceId(),
            });
            if (submitRes.job_id) batchJobIds.push(submitRes.job_id);
        }
        uploadAbort = null;
        paintProgress(100, statusSnapshot, 'optimizing');
        optimizationTimedOut = false;
        pollStartedAt = Date.now();
        pollTimer = setInterval(pollStatus, POLL_INTERVAL_MS);
        pollStatus();
    }

    async function claimProject(delivery) {
        return apiPost('claim', {
            project_name: delivery.name,
            delivery_mode: mode,
            client_email: delivery.to,
            message: delivery.msg,
            job_ids: batchJobIds,
        }, csrfToken);
    }

    function deliverableJobIdsFromStatus(status) {
        const fromStatus = (status?.jobs || [])
            .filter((j) => isTerminalJobStatus(j.status))
            .map((j) => j.job_id);
        return fromStatus.length ? fromStatus : batchJobIds.slice();
    }

    async function downloadOptimizedFiles() {
        if (!downloadContext?.jobIds?.length) return;

        const { jobIds, projectName } = downloadContext;
        const btn = $('dlBtn');
        const prevText = btn?.textContent;

        if (btn) {
            btn.textContent = jobIds.length === 1 ? 'Starting download…' : 'Preparing download…';
        }

        try {
            const totalBytes = (statusSnapshot.input_bytes || files.reduce((s, f) => s + f.size, 0));
            const total_mb = Math.round(totalBytes / (1024 * 1024));
            const zipDownloadProps = {
                download_type: 'zip',
                file_count: jobIds.length,
                total_mb,
            };
            trackHomeEvent('image_result_downloaded', zipDownloadProps);
            trackAnyResultDownloaded(zipDownloadProps);
            if (jobIds.length === 1) {
                downloadUrl(`${config.apiUrl}/jobs/${jobIds[0]}/download`);
            } else {
                const { url } = await zipJobs({ job_ids: jobIds });
                downloadUrl(url, `${projectName || 'jpegmini-assets'}.zip`);
            }
        } catch (err) {
            console.error(err);
            if ($('dLead')) {
                $('dLead').textContent = 'There was a problem preparing the download. Try the button again.';
            }
        } finally {
            if (btn) {
                btn.textContent = prevText || 'Download optimized files';
            }
        }
    }

    async function finalizeDelivery(delivery, status) {
        if ($('waitNote')) $('waitNote').textContent = 'Finishing up…';
        const claim = await claimProject(delivery);
        const origin = window.location.origin;
        const shareUrl = claim.share_url ? `${origin}${claim.share_url}` : '';
        const input = status.input_bytes || 0;
        const output = status.output_bytes || input;

        doneProjectId = claim.project_id || null;
        doneProjectName = delivery.name || '';
        doneShareUrl = shareUrl;

        keepOpen('');
        const light = lighterPct(input, output);
        const sizeLine = `<b>${gb(input)}</b> became <b>${gb(output)}</b>${light ? `, ${light}% lighter` : ''}.`;
        busy = false;

        if (mode === 'download') {
            $('dH').textContent = 'Your files are ready';
            $('dLead').innerHTML = sizeLine;
            $('dLink').hidden = true;
            $('dDown').hidden = false;
            downloadContext = {
                jobIds: deliverableJobIdsFromStatus(status),
                projectName: delivery.name,
            };
            downloadOptimizedFiles();
        } else {
            $('dH').textContent = mode === 'email' ? `Sent to ${delivery.to}` : 'Your gallery is ready';
            $('dLead').innerHTML = `${sizeLine}<br>${mode === 'email' ? 'Your gallery is saved to your account.' : 'Copy it or share it, wherever your client is.'}`;
            $('dLink').hidden = false;
            $('dDown').hidden = true;
            applyShareUrlToUi(shareUrl);
        }
        queuedDelivery = null;
        show('pDone', { email: 'doneEmail', download: 'doneDownload' }[mode] || 'done');
        stopStatusPolling();
    }

    function nice(n) {
        return (n || '').replace(/[_\-.]+/g, ' ').replace(/\s+/g, ' ').trim();
    }

    function take(list, method = uploadMethod) {
        uploadMethod = method;
        const r = { bytes: 0 };
        const accepted = [];
        const skipped = [];
        Array.from(list).forEach((f) => {
            if (isVid(f) || isHeic(f) || isJpg(f)) {
                accepted.push(f);
                r.bytes += f.size;
            } else if (!/^\./.test(f.name)) {
                skipped.push(f);
            }
        });
        if (!accepted.length) {
            $('addErr').textContent = skipped.length
                ? unsupportedMessage(skipped)
                : 'That folder is empty. Add JPEG, HEIC, MP4 or MOV files.';
            return;
        }
        if (r.bytes > CAP) {
            $('addErr').textContent = `That's ${gb(r.bytes)}. Free sends hold up to 5 GB.`;
            return;
        }
        $('addErr').textContent = '';
        files = accepted;
        trackUploadStarted();
        if (window.scrollY > 0) window.scrollTo({ top: 0, behavior: 'smooth' });
        start();
        if (skipped.length && $('eWork')) {
            const named = listExts(skipped.map(extOf));
            $('eWork').textContent = `Skipped ${skipped.length} ${skipped.length === 1 ? 'file' : 'files'}${named ? ` (${named})` : ''}. Only JPEG, HEIC, MP4 and MOV are supported.`;
        }
    }

    async function start() {
        batchJobIds = [];
        statusSnapshot = { progress: 0, done: false, input_bytes: 0, output_bytes: 0 };
        busy = true;
        if ($('eWork')) $('eWork').textContent = '';
        show('pWork');
        t0 = Date.now();
        paintProgress(0, statusSnapshot, 'upload');
        ask(verified ? 'name' : 'acct');
        try {
            await uploadAll();
        } catch (err) {
            console.error(err);
            const message = friendlyUploadError(err, currentFile);
            stopStatusPolling();
            uploadAbort?.();
            uploadAbort = null;
            await reset();
            if ($('addErr')) $('addErr').textContent = message;
        }
    }

    async function reset() {
        signupPanelTracked = false;
        busy = false;
        keepOpen('');
        currentFile = null;
        if (acctTitle) acctTitle.textContent = acctTitleDefault;
        if ($('eWork')) $('eWork').textContent = '';
        stopStatusPolling();
        setProgressIndeterminate(false);
        $('ring')?.classList.remove('done');
        optimizationTimedOut = false;
        uploadAbort?.();
        files = [];
        queuedDelivery = null;
        downloadContext = null;
        doneShareUrl = '';
        doneProjectId = null;
        doneProjectName = '';
        if ($('copy')) $('copy').textContent = 'Copy link';
        batchJobIds = [];
        statusSnapshot = { progress: 0, done: false, input_bytes: 0, output_bytes: 0 };
        $('file').value = '';
        ['wname', 'to', 'msg', 'email', 'code'].forEach((id) => { if ($(id)) $(id).value = ''; });
        if ($('fMsg')) $('fMsg').hidden = true;
        if ($('addMsg')) $('addMsg').hidden = false;
        suggested = '';
        if ($('folder')) $('folder').value = '';
        if ($('toast')) $('toast').textContent = '';
        try {
            const data = await apiPost('new-batch', {}, csrfToken);
            publicHomeToken = data.public_home_token || publicHomeToken;
        } catch (err) {
            console.warn('[public-home] new-batch', err);
        }
        show('pAdd');
    }

    async function completeSignup({ email, userId, method, created }) {
        if (userId) identifyPublicHomeUser(userId);
        const variant = getPublicHomeVariant();
        const source = signupSourceForVariant(variant.vKey, variant.V?.signupSource);
        trackHomeSignupClient({
            method,
            created,
            source,
            pageVariant: variant.vKey,
            useCase: variant.useCase,
        });
        signedIn(email);
    }

    function signedIn(email) {
        verified = true;
        userEmail = email;
        $('email').value = email;
        $('whoami').textContent = email;
        if ($('navSignin')) {
            $('navSignin').textContent = 'My galleries';
            $('navSignin').href = '/galleries';
        }
        if (sayNow === 'work') say('workIn');
        ask('name');
    }

    $('addBtn')?.addEventListener('click', () => $('file')?.click());
    $('file')?.addEventListener('change', function () {
        if (this.files?.length) { suggested = ''; take(this.files, 'picker'); }
    });
    $('folderBtn')?.addEventListener('click', () => $('folder')?.click());
    $('folder')?.addEventListener('change', function () {
        if (!this.files?.length) return;
        const rel = this.files[0].webkitRelativePath || '';
        suggested = nice(rel.split('/')[0]);
        take(this.files, 'folder');
    });

    function walk(entry, out) {
        return new Promise((res) => {
            if (entry.isFile) {
                return entry.file((f) => { out.push(f); res(); }, () => res());
            }
            const reader = entry.createReader();
            const all = [];
            const more = () => {
                reader.readEntries((batch) => {
                    if (!batch.length) {
                        return Promise.all(all.map((e) => walk(e, out))).then(res);
                    }
                    all.push(...batch);
                    more();
                }, () => res());
            };
            more();
        });
    }

    function fromDrop(dt) {
        const items = dt.items ? Array.from(dt.items) : [];
        const entries = items.map((it) => (it.webkitGetAsEntry ? it.webkitGetAsEntry() : null)).filter(Boolean);
        const dir = entries.find((e) => e.isDirectory);
        if (!dir) {
            suggested = '';
            return take(dt.files, 'drop');
        }
        suggested = nice(dir.name);
        const out = [];
        Promise.all(entries.map((e) => walk(e, out))).then(() => take(out, 'drop'));
    }

    let depth = 0;
    window.addEventListener('dragenter', (e) => {
        e.preventDefault();
        if (!$('pAdd')?.hidden) { depth += 1; $('dz')?.classList.add('on'); }
    });
    window.addEventListener('dragover', (e) => e.preventDefault());
    window.addEventListener('dragleave', () => {
        depth = Math.max(0, depth - 1);
        if (!depth) $('dz')?.classList.remove('on');
    });
    window.addEventListener('drop', (e) => {
        e.preventDefault();
        depth = 0;
        $('dz')?.classList.remove('on');
        if (!$('pAdd')?.hidden && e.dataTransfer?.files?.length) fromDrop(e.dataTransfer);
    });

    $('cancel')?.addEventListener('click', reset);

    $('google')?.addEventListener('click', () => {
        trackHomeEvent('auth_started', { page: 'home', method: 'google' });
        const src = getPublicHomeVariant().V?.signupSource;
        const q = src ? `&signupSource=${encodeURIComponent(src)}` : '';
        const w = window.open(`/auth/google?popup=1${q}`, 'jm-google-auth', 'width=520,height=640');
        if (!w) {
            $('eAcct').textContent = 'Allow popups to sign in with Google.';
        }
    });

    window.addEventListener('message', async (event) => {
        if (event.origin !== window.location.origin) return;
        if (event.data?.type === 'jm-auth' && event.data.ok) {
            try {
                const res = await fetch('/api/user');
                const user = await res.json();
                await completeSignup({
                    email: user?.email || $('email').value.trim() || 'your account',
                    userId: user?.id,
                    method: 'google',
                    created: Boolean(event.data.created),
                });
            } catch {
                await completeSignup({
                    email: $('email').value.trim() || 'your account',
                    method: 'google',
                    created: Boolean(event.data.created),
                });
            }
        }
    });

    document.querySelector('[data-q="acct"]')?.addEventListener('submit', async (e) => {
        e.preventDefault();
        const v = $('email').value.trim();
        if (!okEmail(v)) {
            $('eAcct').textContent = v ? 'That email doesn\'t look right.' : 'Enter your email.';
            return;
        }
        $('eAcct').textContent = '';
        trackHomeEvent('auth_started', { page: 'home', method: 'email' });
        const startRes = await fetch('/auth/passwordless/start', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'x-csrf-token': csrfToken },
            body: JSON.stringify({ email: v }),
        });
        const startData = await startRes.json();
        if (!startRes.ok) {
            $('eAcct').textContent = startData.error || 'Could not send code.';
            return;
        }
        $('codeTo').textContent = v;
        $('code').value = '';
        ask('code');
    });

    $('code')?.addEventListener('input', async function () {
        this.value = this.value.replace(/\D/g, '').slice(0, 4);
        if (this.value.length !== 4) return;
        const email = $('email').value.trim();
        const res = await fetch('/auth/passwordless/verify', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'x-csrf-token': csrfToken },
            body: JSON.stringify({
                email,
                otp: this.value,
                embedHome: true,
                signupSource: getPublicHomeVariant().V?.signupSource || undefined,
                mixpanel_device_id: getMixpanelDeviceId(),
            }),
        });
        const data = await res.json();
        if (!res.ok) {
            $('eCode').textContent = data.error || 'Invalid code';
            return;
        }
        $('eCode').textContent = '';
        await completeSignup({
            email,
            userId: data.user?.id,
            method: 'email',
            created: Boolean(data.created),
        });
    });

    document.querySelector('[data-q="name"]')?.addEventListener('submit', (e) => {
        e.preventDefault();
        if (!$('wname').value.trim()) {
            $('eName').textContent = getPublicHomeVariant().T?.eName || 'Give it a name, so you and your client can find it.';
            return;
        }
        $('eName').textContent = '';
        ask('to');
    });

    document.querySelectorAll('.opt[data-m]').forEach((o) => {
        o.addEventListener('click', () => setMode(o.dataset.m));
    });

    $('addMsg')?.addEventListener('click', function () {
        this.hidden = true;
        if ($('fMsg')) $('fMsg').hidden = false;
        focusInCard($('msg'));
    });

    document.querySelector('[data-q="to"]')?.addEventListener('submit', async (e) => {
        e.preventDefault();
        const delivery = {
            name: $('wname').value.trim(),
            to: $('to').value.trim(),
            msg: $('msg').value.trim(),
        };
        if (mode === 'email' && !okEmail(delivery.to)) {
            $('eToMail').textContent = delivery.to
                ? 'That email doesn\'t look right.'
                : (getPublicHomeVariant().T?.eTo || 'Add your client\'s email, or choose Give me the link.');
            focusInCard($('to'));
            return;
        }
        $('eTo').textContent = '';
        $('eToMail').textContent = '';
        trackHomeEvent('home_next_step_chosen', { choice: mode });
        if (!verified) {
            $('eTo').textContent = 'Sign in to continue.';
            ask('acct');
            return;
        }
        queuedDelivery = delivery;
        if (statusSnapshot.done) {
            const jobs = statusSnapshot.jobs || [];
            const deliverable = jobs.filter((j) => isTerminalJobStatus(j.status));
            if (!deliverable.length) {
                showWorkError('Nothing finished optimizing yet.');
                return;
            }
            try {
                await finalizeDelivery(delivery, statusSnapshot);
            } catch (err) {
                console.error(err);
                showWorkError(err.message || 'Could not finish setup. Try again.');
            }
            return;
        }
        $('waitNote').textContent = {
            email: `All set. We'll send it to ${delivery.to} the moment it's done.`,
            link: 'All set. Your link appears the moment it\'s done.',
            download: 'All set. Your download starts the moment it\'s done.',
        }[mode];
        ask('wait');
    });

    $('dlBtn')?.addEventListener('click', (e) => {
        e.preventDefault();
        downloadOptimizedFiles();
    });

    $('toLink')?.addEventListener('click', () => {
        void showShareLinkAfterDownload();
    });

    $('copy')?.addEventListener('click', function () {
        copyShareLink(this);
    });

    $('share')?.addEventListener('click', () => {
        shareShareLink();
    });

    $('again')?.addEventListener('click', reset);
    $('again2')?.addEventListener('click', reset);

    // Back buttons (step 3 → step 2)
    document.querySelectorAll('[data-back]').forEach((b) => {
        b.addEventListener('click', () => ask(b.dataset.back));
    });

    // "use a different email" on the code step
    $('chg')?.addEventListener('click', () => {
        if ($('code')) $('code').value = '';
        ask('acct');
        setTimeout(() => $('email')?.select(), 80);
    });

    // Clear an error as soon as the user edits the field it refers to
    [['email', 'eAcct'], ['code', 'eCode'], ['wname', 'eName'], ['to', 'eToMail']].forEach(([field, err]) => {
        $(field)?.addEventListener('input', () => { if ($(err)) $(err).textContent = ''; });
    });

    // In-page links that leave the page: our own dialog (the browser's tab-close prompt can't be customised)
    let leaveHref = null;
    document.addEventListener('click', (e) => {
        if (!busy || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        const a = e.target.closest && e.target.closest('a[href]');
        if (!a || a.target === '_blank' || a.hasAttribute('download')) return;
        const href = a.getAttribute('href') || '';
        if (!href || href.startsWith('#') || href.startsWith('javascript:')) return;
        const dlg = $('leaveDlg');
        if (!dlg || typeof dlg.showModal !== 'function') return;
        e.preventDefault();
        leaveHref = a.href;
        $('leaveP').textContent = statusSnapshot.done
            ? 'Your files are ready but not sent yet. If you leave, you\'ll need to add them again.'
            : 'Your upload will stop, and you\'ll need to add the files again.';
        dlg.showModal();
        $('leaveStay')?.focus();
    }, true);
    $('leaveStay')?.addEventListener('click', () => $('leaveDlg')?.close());
    $('leaveGo')?.addEventListener('click', () => {
        busy = false;
        $('leaveDlg')?.close();
        if (leaveHref) window.location.href = leaveHref;
    });

    function maybeTrackAbandon() {
        if (abandonSent || !busy || verified) return;
        abandonSent = true;
        const { batch_size } = batchStats();
        const variant = getPublicHomeVariant();
        trackHomeAbandon({
            stage: statusSnapshot.done ? 'ready' : 'uploading',
            batch_size,
            seconds_on_card: t0 ? Math.round((Date.now() - t0) / 1000) : 0,
            page_variant: variant.vKey,
            ...(variant.useCase ? { use_case: variant.useCase } : {}),
        });
    }

    // Warn before leaving while files are uploading or waiting to be sent
    window.addEventListener('beforeunload', (e) => {
        if (!busy) return;
        maybeTrackAbandon();
        e.preventDefault();
        e.returnValue = '';
    });
    window.addEventListener('pagehide', () => {
        maybeTrackAbandon();
    });

    // Every start button (nav, pricing, closing CTA, footer): scroll up, flash the card, focus +
    document.querySelectorAll('a[href="#send"]').forEach((a) => {
        a.addEventListener('click', (e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: 'smooth' });
            const card = document.querySelector('.card');
            if (card) {
                card.classList.remove('flash');
                void card.offsetWidth;
                card.classList.add('flash');
            }
            if (!$('pAdd')?.hidden) setTimeout(() => $('addBtn')?.focus({ preventScroll: true }), 450);
        });
    });
}
